import L from 'leaflet';
import { Configuration, GeocodingApi } from '@stadiamaps/api';
import 'leaflet/dist/leaflet.css';

let stadiaGeocodingClient = null;
let stadiaGeocodingClientKey = null;
let stadiaGeocodingClientBaseUrl = null;
const STADIA_EUROPE_BOUNDARY_COUNTRIES = [
    'AL', 'AD', 'AM', 'AT', 'AZ', 'BA', 'BE', 'BG', 'BY', 'CH', 'CY', 'CZ',
    'DE', 'DK', 'EE', 'ES', 'FI', 'FO', 'FR', 'GB', 'GE', 'GG', 'GI', 'GR',
    'HR', 'HU', 'IE', 'IM', 'IS', 'IT', 'JE', 'LI', 'LT', 'LU', 'LV', 'MC',
    'MD', 'ME', 'MK', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'SE', 'SI',
    'SJ', 'SK', 'SM', 'TR', 'UA', 'VA',
];
const STADIA_EUROPE_BOUNDARY_RECT = {
    minLat: 34.0,
    minLon: -31.5,
    maxLat: 72.5,
    maxLon: 45.5,
};
const getStadiaApiBaseUrl = () => (
    window.appConfig?.stadiaApiBaseUrl ||
    import.meta.env.VITE_STADIA_API_BASE_URL ||
    'https://api-eu.stadiamaps.com'
);

const getStadiaBoundaryCountries = () => {
    const sanitizeBoundaryCountries = (countries) => countries
        .map((country) => String(country).trim().toUpperCase())
        .filter((country) => country && country !== 'XK');

    const configuredBoundaryCountries =
        window.appConfig?.stadiaBoundaryCountries ||
        import.meta.env.VITE_STADIA_BOUNDARY_COUNTRIES;

    if (Array.isArray(configuredBoundaryCountries) && configuredBoundaryCountries.length) {
        return sanitizeBoundaryCountries(configuredBoundaryCountries);
    }

    if (typeof configuredBoundaryCountries === 'string' && configuredBoundaryCountries.trim()) {
        return sanitizeBoundaryCountries(configuredBoundaryCountries.split(','));
    }

    return sanitizeBoundaryCountries(STADIA_EUROPE_BOUNDARY_COUNTRIES);
};

const PLACEHOLDER_ENV_VALUE_PATTERN = /^\{[A-Z0-9_]+\}$|^\$\{[A-Z0-9_]+\}$/;

const resolveStadiaApiKey = () => {
    const rawValue =
        window.appConfig?.stadiaApiKey ||
        import.meta.env.VITE_STADIA_KEY ||
        '';

    const apiKey = typeof rawValue === 'string' ? rawValue.trim() : '';
    if (!apiKey || PLACEHOLDER_ENV_VALUE_PATTERN.test(apiKey)) {
        return '';
    }

    return apiKey;
};

const getStadiaGeocodingClient = (apiKey = '') => {
    const basePath = getStadiaApiBaseUrl();

    if (
        stadiaGeocodingClient &&
        stadiaGeocodingClientKey === apiKey &&
        stadiaGeocodingClientBaseUrl === basePath
    ) {
        return stadiaGeocodingClient;
    }

    stadiaGeocodingClientKey = apiKey;
    stadiaGeocodingClientBaseUrl = basePath;

    stadiaGeocodingClient = new GeocodingApi(new Configuration({
        basePath,
        ...(apiKey ? { apiKey } : {}),
    }));

    return stadiaGeocodingClient;
};

const registerLocationPicker = () => {
    if (window.__locationPickerRegistered) {
        return;
    }

    window.__locationPickerRegistered = true;
    window.Alpine.data('locationPicker', (params) => window.locationPicker(params));
};

document.addEventListener('alpine:init', registerLocationPicker);

if (window.Alpine) {
    registerLocationPicker();
}

window.locationPicker = ({ locations, selected, statePath }) => ({
    // --- reactive state ---
    locations: Array.isArray(locations) ? locations : Object.values(locations ?? {}),
    filteredLocations: [],
    selected,
    statePath,
    map: null,
    markers: [],
    userMarker: null,
    addressQuery: '',
    addressResults: [],
    addressActiveIndex: -1,
    mapVisibilityInterval: null,
    isMobile: window.innerWidth < 640,
    popupOffset: window.innerWidth < 640 ? -8 : -12, // centralized popup offset

    // --- Lazy init (ensures container visible) ---
    initDeferred() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    observer.disconnect();
                    this.init();
                }
            });
        }, { threshold: 0.2 });
        observer.observe(this.$el);
    },

    // --- Lifecycle ---
    init() {
        if (this._initialized && this.map) {
            return;
        }

        this.filteredLocations = this.locations;

        this.$nextTick(() => {
            this.initializeMap();

            if (!this.map) {
                return;
            }

            if (!this._initialized) {
                this._initialized = true;
                if (!this.selected) {
                    setTimeout(() => this.locateUser(), 1000);
                }

                if (!this._resizeListenerAttached) {
                    this._resizeListenerAttached = true;
                    window.addEventListener('resize', () => {
                        const nowMobile = window.innerWidth < 640;
                        if (nowMobile !== this.isMobile) {
                            this.isMobile = nowMobile;
                            this.popupOffset = nowMobile ? -8 : -12;
                            this.initializeMap();
                        } else {
                            this.map.invalidateSize();
                        }
                    });
                }
            }
        });

        if (this.selected) {
            this.$nextTick(() => this.selectLocation(this.selected, 13));
        }
    },

    // --- Map setup ---
    initializeMap() {
        const isMobile = window.innerWidth < 640;
        const mapId = isMobile ? '#map-mobile' : '#map-desktop';
        const mapEl = this.$el.querySelector(mapId);

        if (!mapEl) return;

        if (this.map) {
            this.map.remove();
            this.map = null;
        }
        this.clearMapVisibilityInterval();

        this.map = L.map(mapEl, {
            scrollWheelZoom: false,
            doubleClickZoom: true,
            dragging: !L.Browser.mobile,
            tap: !L.Browser.mobile
        }).setView([51.1657, 10.4515], 13);
        mapEl._leaflet_map_instance = this.map;

        this.map.attributionControl.setPrefix('');

        const stadiaApiKey = resolveStadiaApiKey();

        const baseUrl = 'https://tiles-eu.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}.png';
        const tileUrl = stadiaApiKey
            ? `${baseUrl}?language=de&api_key=${stadiaApiKey}`
            : `${baseUrl}?language=de`;

        L.tileLayer(tileUrl, {
            maxZoom: 19,
            attribution:
                '&copy; <a href="https://stadiamaps.com/" target="_blank" rel="noopener noreferrer">Stadia Maps</a> | ' +
                '&copy; <a href="https://openmaptiles.org/" target="_blank" rel="noopener noreferrer">OpenMapTiles</a> | ' +
                '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
        }).addTo(this.map);

        this.renderMarkers();
        setTimeout(() => this.map.invalidateSize(), 400);
        this.ensureMapVisibility(mapEl);
    },

    // --- Manual selection (list or marker) ---
    selectLocation(id, zoomOverride = null, setSelected = true) {
        const loc = this.locations.find(l => l.id === id);
        if (!loc || !this.map) return;

        if (setSelected) {

            this.selected = id;

            if (this.statePath && this.$wire) {
                this.$wire.set(this.statePath, id);
            }

            this.$nextTick(() => {
                this.scrollSelectedIntoView();
                setTimeout(() => this.scrollSelectedIntoView(), 50);
            });
        }

        const marker = this.markers.find(m => {
            const { lat, lng } = m.getLatLng();
            return Math.abs(lat - loc.lat) < 0.0001 && Math.abs(lng - loc.lng) < 0.0001;
        });

        // Calculate distance dynamically if user located
        if (!loc.distance && this.userMarker) {
            const { lat, lng } = this.userMarker.getLatLng();
            loc.distance = this.haversine(lat, lng, loc.lat, loc.lng);
        }

        const zoom = zoomOverride ?? 15;
        this.map.flyTo([loc.lat, loc.lng], zoom, { animate: true, duration: 0.8 });

        const popupHtml = this.buildLocationPopupHtml(loc);

        this.map.once('moveend', () => {
            (marker
                    ? marker.bindPopup(popupHtml, this.getPopupOptions())
                    : L.popup(this.getPopupOptions())
                    .setLatLng([loc.lat, loc.lng])
                    .setContent(popupHtml)
            ).openPopup();
        });
    },

    // --- Scroll list to selected item ---
    scrollSelectedIntoView() {
        if (!this.selected) return;

        const desktopList = this.$el.querySelector('.sm\\:flex .flex-1.overflow-y-auto');
        const desktopItem = desktopList?.querySelector(`[data-loc-id="${this.selected}"]`);

        if (desktopList && desktopItem) {
            desktopItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }

        const mobileList = this.$el.querySelector('.sm\\:hidden .overflow-y-auto.h-full');
        const mobileItem = mobileList?.querySelector(`[data-loc-id="${this.selected}"]`);

        if (mobileList && mobileItem) {
            mobileItem.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    },

    // --- Render all markers ---
    renderMarkers() {
        this.markers.forEach(m => this.map.removeLayer(m));
        this.markers = [];

        const customMarker = L.divIcon({
            className: 'custom-marker',
            html: `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                     fill="rgb(119, 41, 190)" width="28" height="28">
                    <path fill-rule="evenodd"
                          d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742
                             19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0
                             c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742Z
                             M12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd"/>
                </svg>
            `,
            iconSize: [28, 28],
            iconAnchor: [14, 28],
            popupAnchor: [0, 0],
        });

        if (!Array.isArray(this.filteredLocations)) return;

        this.filteredLocations.forEach((loc, idx) => {
            if (!loc.lat || !loc.lng) return;

            const marker = L.marker([loc.lat, loc.lng], { icon: customMarker })
            .addTo(this.map)
            .bindPopup(this.buildLocationPopupHtml(loc, idx + 1), this.getPopupOptions())
            .on('click', () => this.selectLocation(loc.id));

            this.markers.push(marker);
        });

        if (this.markers.length > 0) {
            const group = L.featureGroup(this.markers);
            this.map.fitBounds(group.getBounds().pad(0.1));
        }
    },

    // --- Haversine (distance km) ---
    haversine(lat1, lon1, lat2, lon2) {
        const R = 6371;
        const toRad = deg => deg * (Math.PI / 180);
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    },

    formatDistance(distance) {
        return Number(distance).toLocaleString('de-DE', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        });
    },

    getPopupOptions() {
        return {
            offset: [0, this.popupOffset],
            autoPan: false,
        };
    },

    buildLocationPopupHtml(loc, index = null) {
        const title = index ? `${index}. ${loc.name}` : loc.name;
        const distanceHtml = loc.distance
            ? `<div class="text-primary-500 text-sm">${this.formatDistance(loc.distance)} km entfernt</div>`
            : '';

        return `
            <div class="flex flex-col space-y-2 ">
                <div class="font-semibold">${title}</div>
                <div>${loc.address ?? ''}</div>
                ${distanceHtml}
            </div>
        `;
    },

    // --- Locate user via browser API ---
    locateUser() {

        if (!navigator.geolocation) {
            console.warn('Geolocation not supported.');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;

                const userIcon = L.divIcon({
                    className: 'user-marker',
                    html: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="rgb(119, 41, 190)">
                             <circle cx="7" cy="7" r="6" opacity="0.25"/>
                             <circle cx="7" cy="7" r="3"/>
                           </svg>`,
                    iconSize: [14, 14],
                    iconAnchor: [7, 7],
                });

                if (this.userMarker) this.map.removeLayer(this.userMarker);
                this.userMarker = L.marker([lat, lng], { icon: userIcon }).addTo(this.map);

                this.filteredLocations = this.locations.map(l => ({
                    ...l,
                    distance: this.haversine(lat, lng, l.lat, l.lng),
                })).sort((a, b) => a.distance - b.distance);

                this.renderMarkers();

                this.selectNearestLocation(lat, lng);
            },
            (err) => console.warn('Geolocation error:', err.message),
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 },
        );
    },

    // --- Find nearest and center map softly ---
    selectNearestLocation(lat, lng) {
        if (!this.locations?.length) return;

        let nearest = null;
        let minDist = Infinity;

        this.locations.forEach((loc) => {
            const dist = this.haversine(lat, lng, loc.lat, loc.lng);
            if (dist < minDist) {
                minDist = dist;
                nearest = loc;
            }
        });

        if (nearest) {
            // Only center map, don't select yet
            this.selectLocation(nearest.id, 11, false);
            return nearest;
        }

        return null;
    },

    ensureMapVisibility(mapEl) {
        if (!mapEl || !this.map) {
            return;
        }

        const checkSize = () => {
            if (mapEl.clientWidth > 0 && mapEl.clientHeight > 0) {
                this.map.invalidateSize();
            }
        };

        this.clearMapVisibilityInterval();

        this.mapVisibilityInterval = setInterval(() => {
            if (mapEl.clientWidth > 0 && mapEl.clientHeight > 0) {
                this.map.invalidateSize();
                this.clearMapVisibilityInterval();
            }
        }, 250);

        setTimeout(() => {
            this.clearMapVisibilityInterval();
        }, 2500);
    },

    clearMapVisibilityInterval() {
        if (this.mapVisibilityInterval) {
            clearInterval(this.mapVisibilityInterval);
            this.mapVisibilityInterval = null;
        }
    },

    isSelectableAddressResult(result) {
        return Boolean(result) && !result.loading && !result.notFound && !result.error;
    },

    getFirstSelectableAddressResultIndex() {
        return this.addressResults.findIndex((result) => this.isSelectableAddressResult(result));
    },

    ensureAddressActiveIndex() {
        if (this.addressActiveIndex >= 0 && this.isSelectableAddressResult(this.addressResults[this.addressActiveIndex])) {
            return;
        }

        this.addressActiveIndex = this.getFirstSelectableAddressResultIndex();
    },

    moveAddressActiveIndex(step) {
        if (!this.addressResults.length) {
            this.addressActiveIndex = -1;
            return;
        }

        const selectableCount = this.addressResults.filter((result) => this.isSelectableAddressResult(result)).length;
        if (!selectableCount) {
            this.addressActiveIndex = -1;
            return;
        }

        let nextIndex = this.addressActiveIndex;

        if (nextIndex < 0 || !this.isSelectableAddressResult(this.addressResults[nextIndex])) {
            nextIndex = step > 0 ? -1 : this.addressResults.length;
        }

        for (let i = 0; i < this.addressResults.length; i += 1) {
            nextIndex = (nextIndex + step + this.addressResults.length) % this.addressResults.length;
            if (this.isSelectableAddressResult(this.addressResults[nextIndex])) {
                this.addressActiveIndex = nextIndex;
                this.scrollActiveAddressResultIntoView();
                return;
            }
        }
    },

    scrollActiveAddressResultIntoView() {
        this.$nextTick(() => {
            const activeItem = this.$el.querySelector(`[data-address-result-index="${this.addressActiveIndex}"]`);
            activeItem?.scrollIntoView({ block: 'nearest' });
        });
    },

    async selectActiveAddressResult() {
        this.ensureAddressActiveIndex();

        const result = this.addressResults[this.addressActiveIndex];
        if (!this.isSelectableAddressResult(result)) {
            return;
        }

        await this.selectAddress(result);
    },

    async handleAddressKeydown(event) {
        if (!this.addressResults.length) {
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveAddressActiveIndex(1);
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveAddressActiveIndex(-1);
            return;
        }

        if (event.key === 'Enter') {
            const activeResult = this.addressResults[this.addressActiveIndex];
            if (!this.isSelectableAddressResult(activeResult)) {
                return;
            }

            event.preventDefault();
            await this.selectActiveAddressResult();
            return;
        }

        if (event.key === 'Escape') {
            this.addressResults = [];
            this.addressActiveIndex = -1;
        }
    },

    // --- Search address and scroll to nearest location ---
    async searchAddress() {
        const query = this.addressQuery?.trim() ?? '';

        if (query.length < 3) {
            this.addressResults = [];
            this.addressActiveIndex = -1;
            return;
        }

        const stadiaApiKey =
            window.appConfig?.stadiaApiKey ||
            import.meta.env.VITE_STADIA_KEY ||
            '';

        const requestId = (this._addressSearchRequestId ?? 0) + 1;
        this._addressSearchRequestId = requestId;
        this.addressActiveIndex = -1;
        this.addressResults = [{ display_name: 'Suche...', loading: true }];

        try {
            const geocodingClient = getStadiaGeocodingClient(stadiaApiKey);
            const allowedBoundaryCountries = new Set(getStadiaBoundaryCountries());
            const response = await geocodingClient.autocompleteV2({
                text: query,
                boundaryCountry: [...allowedBoundaryCountries],
                boundaryRectMinLat: STADIA_EUROPE_BOUNDARY_RECT.minLat,
                boundaryRectMinLon: STADIA_EUROPE_BOUNDARY_RECT.minLon,
                boundaryRectMaxLat: STADIA_EUROPE_BOUNDARY_RECT.maxLat,
                boundaryRectMaxLon: STADIA_EUROPE_BOUNDARY_RECT.maxLon,
                size: 5,
                lang: 'de',
            });

            if (requestId !== this._addressSearchRequestId) {
                return;
            }

            const results = (response?.features ?? [])
                .map((feature, index) => {
                    const [lon, lat] = feature?.geometry?.coordinates ?? [];
                    const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lon);
                    const properties = feature?.properties ?? {};
                    const context = properties.context ?? {};
                    const iso3166A2 = (
                        context.iso3166A2 ??
                        context.iso3166_a2 ??
                        properties.countryCode ??
                        properties.country_code ??
                        null
                    );

                    if (iso3166A2 && !allowedBoundaryCountries.has(String(iso3166A2).toUpperCase())) {
                        return null;
                    }

                    const formattedAddress =
                        properties.formattedAddressLine ??
                        properties.formatted_address_line;
                    const name = properties.name;
                    const coarseLocation =
                        properties.coarseLocation ??
                        properties.coarse_location;
                    const displayName = formattedAddress || [name, coarseLocation].filter(Boolean).join(', ');

                    return {
                        place_id: properties.gid ?? `${index}`,
                        display_name: displayName || 'Unbekannte Adresse',
                        lat: hasCoordinates ? lat : null,
                        lon: hasCoordinates ? lon : null,
                    };
                })
                .filter((result) => Boolean(result?.display_name));

            if (!results.length) {
                this.addressActiveIndex = -1;
                this.addressResults = [{ display_name: 'Keine Ergebnisse gefunden', notFound: true }];
                return;
            }

            this.addressResults = results;
            this.addressActiveIndex = this.getFirstSelectableAddressResultIndex();
        } catch (error) {
            if (requestId !== this._addressSearchRequestId) {
                return;
            }

            console.error('Geocoding error:', error);
            this.addressActiveIndex = -1;
            this.addressResults = [{ display_name: 'Fehler bei der Adresssuche', error: true }];
        }
    },

    // --- Select address from search results ---
    async selectAddress(result) {
        if (!result) return;

        let selectedResult = result;

        if (!Number.isFinite(selectedResult.lat) || !Number.isFinite(selectedResult.lon)) {
            try {
                const stadiaApiKey =
                    window.appConfig?.stadiaApiKey ||
                    import.meta.env.VITE_STADIA_KEY ||
                    '';

                const geocodingClient = getStadiaGeocodingClient(stadiaApiKey);
                const detailResponse = await geocodingClient.placeDetailsV2({
                    ids: [String(selectedResult.place_id)],
                    lang: 'de',
                });

                const detailFeature = detailResponse?.features?.[0];
                const [detailLon, detailLat] = detailFeature?.geometry?.coordinates ?? [];

                if (Number.isFinite(detailLat) && Number.isFinite(detailLon)) {
                    selectedResult = {
                        ...selectedResult,
                        lat: detailLat,
                        lon: detailLon,
                    };
                }
            } catch (error) {
                console.error('Place details error:', error);
            }
        }

        if (!Number.isFinite(selectedResult.lat) || !Number.isFinite(selectedResult.lon)) {
            this.addressResults = [{ display_name: 'Für diesen Treffer sind keine Koordinaten verfügbar', error: true }];
            return;
        }

        // Clear search results
        this.addressResults = [];
        this.addressActiveIndex = -1;

        // Clear search input
        this.addressQuery = '';

        // Create a temporary marker for the selected address
        const searchIcon = L.divIcon({
            className: 'search-marker',
            html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                   fill="#3b82f6" width="28" height="28">
                    <path fill-rule="evenodd"
                         d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742
                         19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0
                         c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742Z
                         M12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clip-rule="evenodd"/>
                </svg>`,
            iconSize: [28, 28],
            iconAnchor: [14, 28],
            popupAnchor: [0, 0],
        });

        // Add temporary marker
        const searchMarker = L.marker([selectedResult.lat, selectedResult.lon], { icon: searchIcon })
            .addTo(this.map)
            .bindPopup(`
                <b>Gesuchte Adresse</b><br>
                ${selectedResult.display_name}
            `, this.getPopupOptions())
            .openPopup();

        // Fly to the search result
        this.map.flyTo([selectedResult.lat, selectedResult.lon], 14, { animate: true, duration: 0.8 });

        // Find nearest location to this address and select it
        setTimeout(() => {
            // Find the nearest location
            const nearest = this.selectNearestLocation(selectedResult.lat, selectedResult.lon);

            if (nearest) {
                // Actually select the location
                this.selected = nearest.id;

                // Update Livewire model if it exists
                if (this.statePath && this.$wire) {
                    this.$wire.set(this.statePath, nearest.id);
                }

                // Scroll to the selected item in the list (mobile and desktop)
                this.$nextTick(() => {
                    this.scrollSelectedIntoView();
                    setTimeout(() => this.scrollSelectedIntoView(), 50);

                    // Ensure mobile sheet is open
                    const bottomSheet = this.$el.querySelector('.sm\\:hidden [x-data*="open"]');
                    if (bottomSheet && bottomSheet.__x) {
                        bottomSheet.__x.setCleanState('open', true);
                    }
                });
            }

            // Remove temporary marker after a delay
            setTimeout(() => {
                this.map.removeLayer(searchMarker);
            }, 5000);
        }, 1000);
    },
});

// --- Fix map sizing after Livewire renders ---
document.addEventListener('livewire:load', () => {
    Livewire.hook('message.processed', () => {
        document.querySelectorAll('[x-data*=locationPicker]').forEach((el) => {
            const mapContainer = el.querySelector('.map-container');
            if (mapContainer && mapContainer._leaflet_map_instance) {
                mapContainer._leaflet_map_instance.invalidateSize();
            }
        });
    });
});
